import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Ic } from './icons';

import { PBTpl } from './templatebuilder';
import { useDraggable } from '@dnd-kit/core';
import TextControls from '../components/controllers/TextControls';
import ButtonControls from '../components/controllers/ButtonControls';
import ImageControls from '../components/controllers/ImageControls';
import DividerControls from '../components/controllers/DividerControls';
import SignatureControls from '../components/controllers/SignatureControls';
import CodeControls from '../components/controllers/CodeControls';
import TableControls from '../components/controllers/TableControls';
import { extractStyles, generateInlineStyles, generateContentHTML } from '../utils/common';
import { getV1Drive, postV1Drive } from '@/api-client/api.drive/api';
import { getToken, uploadFiles } from '@erpsquad/common/utils/common';
import { useDropzone } from 'react-dropzone';
import { getApiConfigSafe } from '@erpsquad/common/utils';
import EditorSidebar from '../components/EditorSidebar';

/* ─────────────────────────────────────────────────────────────
   ERPForce PDF Builder — control panel (the left designer rail)
   Classic ERP-designer layout: collapsible sections of plain
   controls. No HTML required — every control writes to data,
   style, frame or flags state. Exposes PBControls.
   ───────────────────────────────────────────────────────────── */

const { useState } = React;


const ACCENTS = ['#1B2C6E', '#1A8A56', '#1F4FA8', '#5535B8', '#156B66', '#A8232A', '#8a540b', '#14171b'];

// shown inside a structure section once the doc has been hand-edited on the page
function CustomNote({ onRevert, what }) {
  return (
    <div className="cx-customnote">
      <p>{what}. To rebuild it with these controls, revert that layout.</p>
      {onRevert && <button className="cx-lockbanner-btn" onClick={onRevert}><Ic k="undo" />Revert to visual builder</button>}
    </div>
  );
}

// ── primitives ───────────────────────────────────────────────
function Section({ id, title, icon, open, onToggle, children, hint }) {
  return (
    <div className={`cx-sec ${open ? 'is-open' : ''}`}>
      <button className="cx-sec-h" onClick={onToggle}>
        <span className="cx-sec-ic"><Ic k={icon} /></span>
        <span className="cx-sec-t">{title}</span>
        <Ic k="chevD" className="cx-sec-chev" />
      </button>
      {open && <div className="cx-sec-body">{hint && <div className="cx-hint">{hint}</div>}{children}</div>}
    </div>
  );
}

function Row({ label, children, sub }) {
  return (
    <div className="cx-row">
      <div className="cx-row-l"><span className="cx-row-lbl">{label}</span>{sub && <span className="cx-row-sub">{sub}</span>}</div>
      <div className="cx-row-c">{children}</div>
    </div>
  );
}

function Toggle({ on, onChange }) {
  return <button className={`cx-switch ${on ? 'is-on' : ''}`} role="switch" aria-checked={on} onClick={() => onChange(!on)}><span className="cx-switch-dot" /></button>;
}

function Seg({ value, options, onChange, full }) {
  return (
    <div className={`cx-seg ${full ? 'is-full' : ''}`}>
      {options.map(o => <button key={o.v} className={value === o.v ? 'is-active' : ''} onClick={() => onChange(o.v)} title={o.label}>{o.icon ? <Ic k={o.icon} /> : o.label}</button>)}
    </div>
  );
}

function Select({ value, options, onChange }) {
  return (
    <select className="cx-select" value={value} onChange={e => onChange(e.target.value)}>
      {options.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
    </select>
  );
}

function Range({ value, min, max, step, onChange, suffix }) {
  return (
    <div className="cx-range">
      <input type="range" min={min} max={max} step={step || 1} value={value} onChange={e => onChange(Number(e.target.value))} />
      <span className="cx-range-v">{value}{suffix || ''}</span>
    </div>
  );
}

function Text({ value, onChange, placeholder, area }) {
  return area
    ? <textarea className="cx-input cx-area" value={value} placeholder={placeholder} onChange={e => onChange(e.target.value)} />
    : <input className="cx-input" value={value} placeholder={placeholder} onChange={e => onChange(e.target.value)} />;
}

function Swatches({ value, onChange }) {
  return (
    <div className="cx-swatches">
      {ACCENTS.map(c => <button key={c} className={`cx-swatch ${value === c ? 'is-sel' : ''}`} style={{ background: c }} onClick={() => onChange(c)} aria-label={c} />)}
    </div>
  );
}

// ── TABLE COLUMNS editor (master template structure) ─────────
function ColumnsEditor({ cols, setCols, itemFields }) {
  const opts = [...itemFields.map(f => ({ v: f.id, label: f.label })), { v: '__custom', label: 'Custom field…' }];
  const patch = (i, p) => setCols(cs => cs.map((c, j) => j === i ? { ...c, ...p } : c));
  const move = (i, d) => setCols(cs => { const a = cs.slice(); const j = i + d; if (j < 0 || j >= a.length) return cs;[a[i], a[j]] = [a[j], a[i]]; return a; });
  const remove = (i) => setCols(cs => cs.filter((_, j) => j !== i));
  const add = () => setCols(cs => [...cs, { id: PBTpl.newId(), field: 'name', label: 'New column', align: 'l' }]);
  return (
    <div className="cx-cols">
      {cols.map((col, i) => (
        <div className="cx-col" key={col.id}>
          <div className="cx-col-top">
            <span className="cx-grip"><Ic k="grip" /></span>
            <input className="cx-input cx-col-lbl" value={col.label} onChange={e => patch(i, { label: e.target.value })} placeholder="Header label" />
            <div className="cx-item-move">
              <button onClick={() => move(i, -1)} disabled={i === 0} title="Move left"><Ic k="arrowUp" /></button>
              <button onClick={() => move(i, 1)} disabled={i === cols.length - 1} title="Move right"><Ic k="arrowDn" /></button>
              <button className="cx-item-del" onClick={() => remove(i)} disabled={cols.length <= 1} title="Remove column"><Ic k="trash" /></button>
            </div>
          </div>
          <div className="cx-col-bind">
            <Select value={col.field} options={opts} onChange={v => patch(i, { field: v })} />
            <Seg value={col.align} options={[{ v: 'l', label: 'L' }, { v: 'r', label: 'R' }]} onChange={v => patch(i, { align: v })} />
          </div>
          {col.field === '__custom' && (
            <div className="cx-col-bind" style={{ marginTop: 6 }}>
              <input className="cx-input" value={col.custom || ''} onChange={e => patch(i, { custom: e.target.value })} placeholder="item field key, e.g. weight" />
              <span className="cx-ph-tag">{`{{this.${col.custom || 'field'}}}`}</span>
            </div>
          )}
        </div>
      ))}
      <button className="cx-add" onClick={add}><Ic k="plus" />Add column</button>
    </div>
  );
}

// ── DRAGGABLE ELEMENTS ───────────────────────────────────────
function DraggableElement({ id, label, icon, imageUrl }: { id: string; label: string; icon?: string; imageUrl?: string }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: imageUrl ? `draggable-image-${id}` : `draggable-${id}`,
    data: { type: id, imageUrl, label }
  });

  return (
    <button
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      style={{
        opacity: isDragging ? 0.5 : 1,
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        padding: '8px 12px',
        backgroundColor: '#ffffff',
        border: '1px solid #e5e7eb',
        borderRadius: '6px',
        color: '#374151',
        cursor: isDragging ? 'grabbing' : 'grab',
        fontSize: '13px',
        fontWeight: 500,
        boxShadow: '0 1px 2px rgba(0,0,0,0.02)',
        transition: 'all 0.2s ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = '#f9fafb';
        e.currentTarget.style.borderColor = '#d1d5db';
        e.currentTarget.style.transform = 'translateY(-1px)';
        e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -1px rgba(0,0,0,0.03)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = '#ffffff';
        e.currentTarget.style.borderColor = '#e5e7eb';
        e.currentTarget.style.transform = 'none';
        e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.02)';
      }}
    >
      {imageUrl ? (
        <img src={imageUrl} alt={label} style={{ height: 20, width: 20, objectFit: 'cover', marginRight: 8, borderRadius: 2 }} />
      ) : (
        <span style={{ color: '#6b7280', display: 'flex', alignItems: 'center' }}><Ic k={icon} /></span>
      )}
      <span style={{ marginLeft: imageUrl ? 0 : 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
    </button>
  );
}

// ── DOCUMENT FIELDS editor (dynamic meta placeholders) ───────
function FieldsEditor({ fields, setFields, metaCatalog }) {
  const opts = [...metaCatalog.map(c => ({ v: c.field, label: c.label })), { v: '__custom', label: 'Custom field…' }];
  const patch = (i, p) => setFields(fs => fs.map((f, j) => j === i ? { ...f, ...p } : f));
  const move = (i, d) => setFields(fs => { const a = fs.slice(); const j = i + d; if (j < 0 || j >= a.length) return fs;[a[i], a[j]] = [a[j], a[i]]; return a; });
  const remove = (i) => setFields(fs => fs.filter((_, j) => j !== i));
  const add = () => setFields(fs => { const c = metaCatalog[0] || { field: 'new', label: 'New', expr: '{{new}}' }; return [...fs, { id: PBTpl.newId(), field: c.field, label: c.label, expr: c.expr }]; });
  const bind = (i, field) => {
    if (field === '__custom') { patch(i, { field: '__custom', expr: fields[i].expr && fields[i].expr.indexOf('{{') === 0 ? fields[i].expr : '{{custom.field}}' }); return; }
    const c = metaCatalog.find(x => x.field === field); if (c) patch(i, { field: c.field, expr: c.expr, label: c.label });
  };
  return (
    <div className="cx-cols">
      {fields.map((f, i) => (
        <div className="cx-col" key={f.id}>
          <div className="cx-col-top">
            <span className="cx-grip"><Ic k="grip" /></span>
            <input className="cx-input cx-col-lbl" value={f.label} onChange={e => patch(i, { label: e.target.value })} placeholder="Field label" />
            <div className="cx-item-move">
              <button onClick={() => move(i, -1)} disabled={i === 0} title="Move up"><Ic k="arrowUp" /></button>
              <button onClick={() => move(i, 1)} disabled={i === fields.length - 1} title="Move down"><Ic k="arrowDn" /></button>
              <button className="cx-item-del" onClick={() => remove(i)} title="Remove field"><Ic k="trash" /></button>
            </div>
          </div>
          <div className="cx-col-bind">
            <Select value={f.field} options={opts} onChange={v => bind(i, v)} />
            {f.field === '__custom'
              ? <input className="cx-input" value={f.expr} onChange={e => patch(i, { expr: e.target.value })} placeholder="{{your.placeholder}}" />
              : <span className="cx-ph-tag">{f.expr}</span>}
          </div>
        </div>
      ))}
      <button className="cx-add" onClick={add}><Ic k="plus" />Add field</button>
    </div>
  );
}

// ── line-items editor (sample values) ────────────────────────
function ItemsEditor({ data, ops }) {
  const items = data.items || [];
  const priced = items.length && 'price' in items[0];
  return (
    <div className="cx-items">
      {items.map((it, i) => (
        <div className="cx-item" key={i}>
          <div className="cx-item-top">
            <span className="cx-item-n">{i + 1}</span>
            <input className="cx-input cx-item-name" value={it.name} onChange={e => ops.field(i, 'name', e.target.value)} placeholder="Description" />
            <div className="cx-item-move">
              <button onClick={() => ops.move(i, -1)} disabled={i === 0} title="Move up"><Ic k="arrowUp" /></button>
              <button onClick={() => ops.move(i, 1)} disabled={i === items.length - 1} title="Move down"><Ic k="arrowDn" /></button>
              <button className="cx-item-del" onClick={() => ops.remove(i)} title="Remove line"><Ic k="trash" /></button>
            </div>
          </div>
          {priced ? (
            <div className="cx-item-grid">
              <label><span>Qty</span><input className="cx-input" type="number" value={it.qty} onChange={e => ops.field(i, 'qty', e.target.value, 'num')} /></label>
              <label><span>Unit</span><input className="cx-input" value={it.unit} onChange={e => ops.field(i, 'unit', e.target.value)} /></label>
              <label><span>Price</span><input className="cx-input" type="number" value={it.price} onChange={e => ops.field(i, 'price', e.target.value, 'num')} /></label>
              <label><span>Disc %</span><input className="cx-input" type="number" value={it.disc} onChange={e => ops.field(i, 'disc', e.target.value, 'num')} /></label>
            </div>
          ) : (
            <div className="cx-item-grid cx-item-grid--2">
              <label><span>Qty</span><input className="cx-input" type="number" value={it.qty} onChange={e => ops.field(i, 'qty', e.target.value, 'num')} /></label>
              <label><span>Unit</span><input className="cx-input" value={it.unit} onChange={e => ops.field(i, 'unit', e.target.value)} /></label>
            </div>
          )}
        </div>
      ))}
      <button className="cx-add" onClick={ops.add}><Ic k="plus" />Add line item</button>
    </div>
  );
}

// ── the panel ────────────────────────────────────────────────
export function ControlsPanel(props) {
  const { doc, data, style, setStyle, frame, setFrame, flags, setFlag, paper, setPaper, orient, setOrient, itemOps, cols, setCols, metaFields, setMetaFields, isCustom, onRevertCustom, stnKind, stnName, onManageStationery, dynamicGroups, arrayKey, activeSectionId, sections, setSections } = props;
  const stnOwned = !!stnKind;   // a Stationery preset / pre-printed / blank owns the masthead + footer

  const activeSection = sections?.find(s => s.id === activeSectionId);
  const isTableActive = activeSection?.type === 'table';

  const [open, setOpen] = useState({ elements: true, content: false, columns: isTableActive, items: false, header: false, footer: false, style: false, page: false, watermark: false });
  const toggle = (k) => setOpen(o => ({ ...o, [k]: !o[k] }));

  const [uploadingWatermark, setUploadingWatermark] = useState(false);
  const [watermarkError, setWatermarkError] = useState('');

  const watermark = style?.watermark || { show: false, opacity: 15, size: 50, rotation: 0, image: '' };
  const updateWatermark = (updates) => {
    if (setStyle) {
      setStyle(s => ({
        ...s,
        watermark: {
          ...(s?.watermark || watermark),
          ...updates
        }
      }));
    }
  };

  const handleWatermarkFile = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      setWatermarkError('Please select an image under 2MB.');
      return;
    }
    setWatermarkError('');
    setUploadingWatermark(true);

    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      updateWatermark({ image: dataUrl, show: true });
    };
    reader.readAsDataURL(file);

    try {
      const uFiles = await uploadFiles([file], 'document');
      if (uFiles && uFiles[0]) {
        const fullUrl = `${getApiConfigSafe().s3BucketUrl}${uFiles[0]}`;
        const token = getToken();
        const drivePayload = {
          ...token,
          'x-timezone': String(token['x-timezone'] || ''),
          title: file?.name?.split('.')[0] || 'watermark',
          path: '/',
          is_private: false,
          is_directory: false,
          file_size: file.size,
          is_child: false,
          file_extension: file?.name?.split('.').pop() || 'png',
          type: 'image',
          mime_type: file.type,
          url: fullUrl,
        };
        await postV1Drive(drivePayload);
        updateWatermark({ image: fullUrl, show: true });
      }
    } catch (err) {
      console.warn('S3 upload for watermark failed, keeping data URL', err);
    } finally {
      setUploadingWatermark(false);
    }
  };

  // Auto-open columns when table clicked
  useEffect(() => {
    if (isTableActive) setOpen(o => ({ ...o, columns: true }));
  }, [isTableActive]);



  const updateActiveTableCols = (newColsOrUpdater) => {
    if (!activeSectionId || !setSections) return;
    setSections(prevSections => prevSections.map(s => {
      if (s.id === activeSectionId && s.type === 'table') {
        const currentCols = s.attributes?.tableData?.columns || [];
        const nextCols = typeof newColsOrUpdater === 'function' ? newColsOrUpdater(currentCols) : newColsOrUpdater;

        // Regenerate the visual HTML table for the canvas
        const ths = nextCols.map(c => `<th style="border:1px solid #ccc; padding:8px; text-align:${c.align === 'r' ? 'right' : c.align === 'c' ? 'center' : 'left'}">${c.label}</th>`).join('');
        const tds = nextCols.map(c => {
          let expr = '{{field}}';
          if (c.field === '__custom') {
            expr = `{{this.${c.custom || 'field'}}}`;
          } else {
            const fieldDef = finalItemFields.find(f => f.id === c.field);
            expr = fieldDef ? fieldDef.expr : `{{this.${c.field}}}`;
          }
          return `<td style="border:1px solid #ccc; padding:8px; text-align:${c.align === 'r' ? 'right' : c.align === 'c' ? 'center' : 'left'}">${expr}</td>`;
        }).join('');
        const newContent = `<table style="width:100%; border-collapse: collapse;"><thead><tr>${ths}</tr></thead><tbody><tr>${tds}</tr></tbody></table>`;

        return {
          ...s,
          content: newContent,
          attributes: {
            ...(s.attributes || {}),
            tableData: {
              ...(s.attributes?.tableData || {}),
              columns: nextCols
            }
          }
        };
      }
      return s;
    }));
  };

  const updateAttrs = (prop) => {
    if (!activeSectionId || !setSections || !activeSection) return;
    setSections(prevSections => prevSections.map(s => {
      if (s.id === activeSectionId) {
        const mergedStyles = { ...(s.attributes?.styles || {}), ...(prop.styles || {}) };
        const mergedAttributes = { ...(s.attributes || {}), ...prop, styles: mergedStyles };
        
        // Sync width and height if updated in styles or directly
        let newWidth = prop.width ?? (typeof prop.styles?.width === 'number' ? prop.styles.width : undefined);
        let newHeight = prop.height ?? (typeof prop.styles?.height === 'number' ? prop.styles.height : undefined);
        let newX = prop.x;

        if (mergedAttributes.styles?.fullWidth && s.type === 'image') {
          newWidth = 794;
          newX = 0;
          mergedStyles.width = 794;
        }

        if (s.type === 'table') {
          const tHeadH = Number(mergedStyles.tHead?.rowHeight) || 38;
          const tBodyH = Number(mergedStyles.tBody?.rowHeight) || 38;
          const numRows = (mergedAttributes.tableData?.rows && mergedAttributes.tableData.rows.length > 0) ? mergedAttributes.tableData.rows.length : 1;
          const calculatedH = tHeadH + (numRows * tBodyH) + 2;
          newHeight = prop.height !== undefined ? prop.height : Math.max(s.height || 0, calculatedH);
        }

        if (newWidth !== undefined) {
          newWidth = Math.round(newWidth);
          mergedStyles.width = newWidth;
        }
        if (newHeight !== undefined) {
          newHeight = Math.round(newHeight);
          mergedStyles.height = newHeight;
        }

        // Dynamic fields processing to pass to generateContentHTML
        const itemsGroup = dynamicGroups?.find(g => g.key === arrayKey);
        const dynamicItemFields = itemsGroup ? itemsGroup.fields.map(f => {
          const fieldId = f.tag.replace(`{{${arrayKey}.[].`, '').replace('}}', '');
          return { id: fieldId, label: f.label, expr: `{{this.${fieldId}}}` };
        }) : ((dynamicGroups && dynamicGroups.length > 0) ? [] : PBTpl.ITEM_FIELDS);
        const currentFinalItemFields = itemsGroup ? [{ id: 'index', label: 'Row number', expr: '{{@number}}' }, ...dynamicItemFields] : ((dynamicGroups && dynamicGroups.length > 0) ? [] : PBTpl.ITEM_FIELDS);
        let currentDynamicMetaFields = [];
        if (dynamicGroups && dynamicGroups.length > 0) {
          dynamicGroups.forEach(g => {
            if (g.key !== arrayKey) {
              g.fields.forEach(f => {
                const fieldId = f.tag.replace('{{', '').replace('}}', '');
                currentDynamicMetaFields.push({ field: fieldId, label: f.label, expr: f.tag });
              });
            }
          });
        }
        const currentFinalMetaFields = (dynamicGroups && dynamicGroups.length > 0) ? currentDynamicMetaFields : PBTpl.META_CATALOG;
        
        const updatedContent = generateContentHTML(s.type as any, s.content, mergedAttributes, [...currentFinalItemFields, ...currentFinalMetaFields]);
        return {
          ...s,
          ...(newWidth !== undefined ? { width: newWidth } : {}),
          ...(newHeight !== undefined ? { height: newHeight } : {}),
          ...(newX !== undefined ? { x: newX } : {}),
          content: updatedContent,
          attributes: mergedAttributes
        };
      }
      return s;
    }));
  };

  const activeTableCols = activeSection?.attributes?.tableData?.columns || [];

  const setH = (patch) => setFrame(f => ({ ...f, header: { ...f.header, ...patch } }));
  const setF = (patch) => setFrame(f => ({ ...f, footer: { ...f.footer, ...patch } }));
  const includes = doc.includes || [];
  const hasItems = Array.isArray(data.items) && data.items.length > 0;
  const H = frame.header, FT = frame.footer;

  const repeatOpts = [{ v: 'all', label: 'All pages' }, { v: 'subsequent', label: 'Continuation pages' }, { v: 'first', label: 'First page only' }];

  // Image Upload State
  const [uploadedImages, setUploadedImages] = useState<any[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getDriveFiles = async () => {
    try {
      const token = getToken();
      const response = await getV1Drive({
        ...token,
        "x-timezone": String(token["x-timezone"] || ""),
        skip: 0,
        limit: 20,
        filters: `(is_directory.eq=0)`
      });
      if (response && 'data' in response && response.data && typeof response.data === 'object' && 'drive' in response.data) {
        setUploadedImages((response.data as any).drive || []);
      }
    } catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    getDriveFiles();
  }, []);

  const onDrop = useCallback(async (acceptedFiles) => {
    setUploading(true);
    setError(null);
    try {
      for (const file of acceptedFiles) {
        const uFiles = await uploadFiles([file], "document");
        const token = getToken();
        const payload = {
          ...token,
          "x-timezone": String(token["x-timezone"] || ""),
          "title": file?.name.split(".")[0],
          "path": "/",
          "is_private": true,
          "is_directory": false,
          "file_size": file.size,
          "is_child": false,
          "file_extension": file?.name.split(".")[1],
          "type": "image",
          "mime_type": file.type,
          "url": `${getApiConfigSafe().s3BucketUrl}${uFiles[0]}`
        }
        await postV1Drive(payload);
        setUploadedImages((prev) => [
          ...prev,
          {
            id: `uploaded-image-${Date.now()}-${Math.random()}`,
            url: `${getApiConfigSafe().s3BucketUrl}${uFiles[0]}`,
            name: file.name,
          },
        ]);
      }
    } catch (err) {
      setError('Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.jpeg', '.png', '.jpg', '.gif'] } as any,
    multiple: true,
  });

  // Dynamic fields processing
  const itemsGroup = dynamicGroups?.find(g => g.key === arrayKey);
  const dynamicItemFields = itemsGroup ? itemsGroup.fields.map(f => {
    const fieldId = f.tag.replace(`{{${arrayKey}.[].`, '').replace('}}', '');
    return { id: fieldId, label: f.label, expr: `{{this.${fieldId}}}` };
  }) : ((dynamicGroups && dynamicGroups.length > 0) ? [] : PBTpl.ITEM_FIELDS);
  const finalItemFields = itemsGroup ? [{ id: 'index', label: 'Row number', expr: '{{@number}}' }, ...dynamicItemFields] : ((dynamicGroups && dynamicGroups.length > 0) ? [] : PBTpl.ITEM_FIELDS);

  let dynamicMetaFields = [];
  if (dynamicGroups && dynamicGroups.length > 0) {
    dynamicGroups.forEach(g => {
      if (g.key !== arrayKey) {
        g.fields.forEach(f => {
          const fieldId = f.tag.replace('{{', '').replace('}}', '');
          dynamicMetaFields.push({ field: fieldId, label: f.label, expr: f.tag });
        });
      }
    });
  }
  const finalMetaFields = (dynamicGroups && dynamicGroups.length > 0) ? dynamicMetaFields : PBTpl.META_CATALOG;

  return (
    <div className="cx">
      {/* ELEMENTS */}
      <Section id="elements" title="Elements" icon="plus" open={open.elements} onToggle={() => toggle('elements')} hint="Drag and drop elements into the canvas.">
        <div style={{ marginTop: '8px' }}>
          <EditorSidebar />
        </div>
      </Section>

      {/* ELEMENT FORMATTING */}
      {activeSection && (
        <Section id="format" title="Format Element" icon="sliders" open={true} onToggle={() => { }} hint="Formatting options for the selected element.">
          {(activeSection.type === 'text' || activeSection.type === 'text-block' || activeSection.type.startsWith('heading')) && (
            <TextControls 
              attrs={activeSection.attributes || {}} 
              updateAttrs={updateAttrs} 
              placeholders={finalMetaFields}
              content={activeSection.content}
              updateContent={(newContent) => {
                if (setSections && activeSectionId) {
                  setSections(prev => prev.map(s => s.id === activeSectionId ? { ...s, content: newContent } : s));
                }
              }}
            />
          )}
          {activeSection.type === 'buttons' && (
            <ButtonControls
              attrs={{
                ...activeSection.attributes,
                styles: {
                  ...(activeSection.attributes?.styles || {}),
                  width: activeSection.width ?? activeSection.attributes?.styles?.width ?? 130,
                  height: activeSection.height ?? activeSection.attributes?.styles?.height ?? 40,
                }
              }}
              updateAttrs={updateAttrs}
            />
          )}
          {activeSection.type === 'image' && (
            <ImageControls
              attrs={{
                ...activeSection.attributes,
                styles: {
                  ...(activeSection.attributes?.styles || {}),
                  width: activeSection.width ?? activeSection.attributes?.styles?.width ?? 200,
                  height: activeSection.height ?? activeSection.attributes?.styles?.height ?? 150,
                }
              }}
              updateAttrs={updateAttrs}
            />
          )}
          {activeSection.type === 'divider' && (
            <DividerControls attrs={activeSection.attributes || {}} updateAttrs={updateAttrs} />
          )}
          {activeSection.type === 'signature' && (
            <SignatureControls attrs={activeSection.attributes || {}} updateAttrs={updateAttrs} />
          )}
          {(activeSection.type === 'qr-code' || activeSection.type === 'bar-code') && (
            <CodeControls attrs={activeSection.attributes || {}} updateAttrs={updateAttrs} />
          )}
          {activeSection.type === 'table' && (
            <TableControls
              key={activeSection.id}
              tableData={activeSection.attributes?.tableData || { columns: [], rows: [] }}
              setTableData={(td) => {
                const currentData = activeSection.attributes?.tableData || { columns: [], rows: [] };
                const nextData = typeof td === 'function' ? td(currentData) : td;
                updateAttrs({ tableData: { ...nextData, name: '' } });
              }}
              attrs={activeSection.attributes || {}}
              updateAttrs={updateAttrs}
              placeholders={finalItemFields}
            />
          )}
        </Section>
      )}

      {/* CONTENT */}
      <Section id="content" title="Content" icon="layers" open={open.content} onToggle={() => toggle('content')}>
        {(!sections || sections.length === 0) ? (
          <div className="cx-hint">Add elements to the canvas to manage their visibility here.</div>
        ) : (
          sections.map((sec, i) => {
            const isHidden = !!sec.hidden;
            const labelName = (sec.type || 'Element').split('-').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
            return (
              <Row key={sec.id} label={`${labelName} ${i + 1}`}>
                <Toggle on={!isHidden} onChange={v => {
                  if (setSections) {
                    setSections((prev: any[]) => prev.map(s => s.id === sec.id ? { ...s, hidden: !v } : s));
                  }
                }} />
              </Row>
            );
          })
        )}
      </Section>

      {/* TABLE COLUMNS (Dynamic Table) */}
      {/* Handled in Format Element via TableControls now */}

      {/* LINE ITEMS (sample values for preview) */}
      {hasItems && (
        <Section id="items" title="Sample data" icon="table" open={open.items} onToggle={() => toggle('items')}
          hint="Sample line items used only to preview the template — the server swaps in real data at render time. Totals recompute live.">
          <ItemsEditor data={data} ops={itemOps} />
        </Section>
      )}
      
      {/* WATERMARK */}
      <Section id="watermark" title="Watermark" icon="image" open={open.watermark} onToggle={() => toggle('watermark')}
        hint="A faint watermark behind page content.">
        <Row label="Show watermark">
          <Toggle on={watermark.show} onChange={v => updateWatermark({ show: v })} />
        </Row>
        {watermark.show && (
          <>
            {/* Watermark Image Upload */}
            <div style={{ padding: '8px 12px', background: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb', margin: '8px 0' }}>
              <span className="cx-row-lbl" style={{ display: 'block', marginBottom: '6px', fontWeight: 600 }}>Watermark Image</span>
              {watermark.image ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{
                    width: '100%',
                    height: '90px',
                    borderRadius: '6px',
                    border: '1px solid #e2e8f0',
                    background: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    position: 'relative'
                  }}>
                    <img
                      src={watermark.image}
                      alt="Watermark preview"
                      style={{
                        maxWidth: '85%',
                        maxHeight: '85%',
                        objectFit: 'contain',
                        opacity: (watermark.opacity ?? 15) / 100,
                        transform: `rotate(${watermark.rotation ?? 0}deg)`
                      }}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <label className="cx-btn cx-btn--sm" style={{ flex: 1, justifyContent: 'center', cursor: 'pointer' }}>
                      <Ic k="image" />
                      {uploadingWatermark ? 'Uploading...' : 'Replace'}
                      <input
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        disabled={uploadingWatermark}
                        onChange={e => {
                          const f = e.target.files?.[0];
                          if (f) handleWatermarkFile(f);
                          e.target.value = '';
                        }}
                      />
                    </label>
                    <button
                      className="cx-btn cx-btn--sm cx-btn--ghost"
                      style={{ color: '#ef4444' }}
                      onClick={() => updateWatermark({ image: '' })}
                      title="Remove watermark image"
                    >
                      <Ic k="trash" /> Remove
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <label
                    style={{
                      border: '2px dashed #cbd5e1',
                      borderRadius: '6px',
                      padding: '16px 12px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: uploadingWatermark ? 'not-allowed' : 'pointer',
                      backgroundColor: '#ffffff',
                      gap: '6px',
                      transition: 'border-color 0.2s',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.borderColor = '#6366f1')}
                    onMouseLeave={e => (e.currentTarget.style.borderColor = '#cbd5e1')}
                  >
                    <Ic k="image" style={{ width: 22, height: 22, color: '#64748b' }} />
                    <span style={{ fontSize: '13px', fontWeight: 500, color: '#334155' }}>
                      {uploadingWatermark ? 'Uploading watermark...' : 'Upload watermark image'}
                    </span>
                    <span style={{ fontSize: '11px', color: '#94a3b8' }}>PNG, JPG, SVG up to 2MB</span>
                    <input
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      disabled={uploadingWatermark}
                      onChange={e => {
                        const f = e.target.files?.[0];
                        if (f) handleWatermarkFile(f);
                        e.target.value = '';
                      }}
                    />
                  </label>
                </div>
              )}
              {watermarkError && (
                <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>{watermarkError}</div>
              )}
            </div>

            <Row label="Opacity">
              <Range value={watermark.opacity ?? 15} min={1} max={100} onChange={v => updateWatermark({ opacity: v })} suffix="%" />
            </Row>
            <Row label="Size">
              <Range value={watermark.size ?? 50} min={10} max={100} onChange={v => updateWatermark({ size: v })} suffix="%" />
            </Row>
            <Row label="Rotation">
              <Range value={watermark.rotation ?? 0} min={-180} max={180} onChange={v => updateWatermark({ rotation: v })} suffix="°" />
            </Row>
          </>
        )}
      </Section>

      {/* PAGE */}
      <Section id="page" title="Page setup" icon="file" open={open.page} onToggle={() => toggle('page')}>
        <Row label="Paper size">
          <Seg full value={paper} options={[{ v: 'A4', label: 'A4' }, { v: 'Letter', label: 'Letter' }]} onChange={setPaper} />
        </Row>
        <Row label="Orientation">
          <Seg full value={orient} options={[{ v: 'portrait', label: 'Portrait' }, { v: 'landscape', label: 'Landscape' }]} onChange={setOrient} />
        </Row>
      </Section>
    </div>
  );
}

export const PBControls = { ControlsPanel };

